/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import AssociatedDevicesGridItem from './AssociatedDevicesGridItem';

export type AssociatedDevice = {
  mobileIdType: string;
  mobileId: string;
  tags: string[];
};

export interface AssociatedDevicesSectionProps {
  associatedDevices?: {
    label: string;
    value: string;
  };
  sentinelDevices?: AssociatedDevice[];
  isLMUDevice?: boolean;
  deviceTags?: string[];
}

const useStyles = makeStyles(() => ({
  headerItem : {
    padding      : '16px 16px 0px',
    color        : dlsColor('grey2'),
    fontSize     : '14px',
    fontFamily   : 'Roboto',
    fontWeight   : 400,
    lineHeight   : '20px !important',
    marginBottom : '8px'
  }
}));

/**
 * Renders a grouped list of associated devices. 
 * Each group is displayed using the `AssociatedDevicesGridItem`
 * component and shown in a full-width grid row with ESN and tag chips.
 *
 * This component is typically used in the Device Details tab to surface associated device
 * relationships like Sentinel pairs and LMU/MDT devices.
 */
const AssociatedDevicesSection = ({
  associatedDevices = {label: '', value: ''},
  sentinelDevices = [],
  isLMUDevice = false,
  deviceTags = []
}: AssociatedDevicesSectionProps) => {
  const classes = useStyles();

  const constructSentinelDevices = useMemo(() => {
    return sentinelDevices.map((device) => ({
      value : device.mobileId,
      tags  : device?.tags || []
    }));
  }, [sentinelDevices]);

  const {
    value: sentinelValue,
    tags: sentinelTags
  } = constructSentinelDevices[0] || {};

  return (
    <Render>
      <Render.If condition={associatedDevices?.value || (sentinelDevices?.length)}>
        <div className={classes.headerItem}>
          {translate('admin-devices:view.generalDeviceInfo.associatedDevices.associatedDevicesTitle')}
        </div>
        <Render.If condition={sentinelDevices?.length}>
          <AssociatedDevicesGridItem labels={translate('admin-devices:view.generalDeviceInfo.associatedDevices', {returnObjects: true})}
            associatedTitle={translate('admin-devices:view.generalDeviceInfo.associatedDevices.associatedSentinel')}
            associatedLabel={translate('admin-devices:view.generalDeviceInfo.associatedDevices.esn')}
            associatedValue={sentinelValue}
            tags={sentinelTags as string[]} />
        </Render.If>
        <Render.If condition={associatedDevices?.value}>
          <AssociatedDevicesGridItem labels={translate('admin-devices:view.generalDeviceInfo.associatedDevices', {returnObjects: true})}
            associatedTitle={translate('admin-devices:view.generalDeviceInfo.associatedDevices.associatedDeviceLabel', {
              category      : translate(`admin-devices:view.generalDeviceInfo.associatedDevices.${isLMUDevice ? 'lmu' : 'mdt'}`, {returnObjects: true}),
              returnObjects : true
            })}
            associatedLabel={associatedDevices?.label}
            associatedValue={associatedDevices?.value}
            tags={deviceTags as string[]} />
        </ Render.If>
      </Render.If>
    </Render>
  );
};

export default AssociatedDevicesSection;
